/**
 * DI-3-S42 — group the drop by FLIGHT AVERAGES (DI-D7 third revision,
 * Christian 2026-10-22: "for us the distance between the averages is the one
 * required").
 *
 * The image-by-image pass (`clusterByPosition`) compares each image with a
 * cluster's running average, so the drone's offset from the tower (up to ≈ a
 * blade length when the blade stands sideways) is subtracted from the
 * spacing between turbines — two turbines 201 m apart (Vienna) became one
 * set at 333 m and would again at 180 m. Averages compared with averages do
 * not lose that offset:
 *
 *   1. set aside ground shots and images without a time (`splitFlights`'
 *      own rules: below `GROUND_CUT_M`, no `capturedAtMs`);
 *   2. split the rest into provisional flights at a gap ≥ `FLIGHT_MERGE_S`
 *      (DI-D39 c4: a battery / rotor change grounds the drone ≥ 3 min);
 *   3. SAFETY NET inside a flight (a pilot who flies two turbines on one
 *      battery): the image pass at `CLUSTER_RADIUS_M` — a flight that splits
 *      there becomes several units;
 *   4. group the units by their AVERAGES within `CLUSTER_RADIUS_M` (greedy in
 *      time order, then the merge pass on the group averages until nothing
 *      merges) — a big rotor's two flights 90 m apart stay one group;
 *   5. every set-aside image joins the group whose average is nearest (its
 *      own position); without any group they are grouped image by image.
 *
 * Output: `PositionCluster[]` as before (ids `c1…` in first-capture order,
 * centroid = the mean of every member, radius = the farthest member).
 */
import {
  byTimeThenIndex,
  clusterByPosition,
  type ClusterPoint,
} from "./cluster.js";
import { FLIGHT_MERGE_S, isGroundShot, type FlightRow } from "./flights.js";
import { haversineM } from "./geo.js";
import { CLUSTER_RADIUS_M } from "./radii.js";
import type { LatLon, PositionCluster } from "./types.js";

interface Group {
  sumLat: number;
  sumLon: number;
  members: ClusterPoint[];
}

function meanOf(g: Group): LatLon {
  return { lat: g.sumLat / g.members.length, lon: g.sumLon / g.members.length };
}

function groupOf(points: readonly ClusterPoint[]): Group {
  let sumLat = 0;
  let sumLon = 0;
  for (const p of points) {
    sumLat += p.lat;
    sumLon += p.lon;
  }
  return { sumLat, sumLon, members: points.slice() };
}

function absorb(into: Group, from: Group): void {
  into.sumLat += from.sumLat;
  into.sumLon += from.sumLon;
  into.members.push(...from.members);
}

/** Step 2 — the time-ordered airborne points split at gaps ≥ `mergeS`. */
function provisionalFlights(
  points: readonly ClusterPoint[],
  mergeS: number,
): ClusterPoint[][] {
  const ordered = points.slice().sort(byTimeThenIndex);
  const out: ClusterPoint[][] = [];
  let current: ClusterPoint[] = [];
  for (const p of ordered) {
    const prev = current[current.length - 1];
    if (prev && (p.t ?? 0) - (prev.t ?? 0) >= mergeS * 1000) {
      out.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length > 0) out.push(current);
  return out;
}

export function groupByFlightAverages(
  points: readonly ClusterPoint[],
  rowOf: ReadonlyMap<number, FlightRow>,
  radiusM: number = CLUSTER_RADIUS_M,
  mergeS: number = FLIGHT_MERGE_S,
): PositionCluster[] {
  // 1
  const airborne: ClusterPoint[] = [];
  const aside: ClusterPoint[] = [];
  for (const p of points) {
    if (p.t === undefined || isGroundShot(rowOf.get(p.index)?.altRel))
      aside.push(p);
    else airborne.push(p);
  }

  // 2 + 3 — the units: a flight, or the parts the safety net split it into
  const byIndex = new Map(points.map((p) => [p.index, p]));
  const units: Group[] = provisionalFlights(airborne, mergeS).flatMap(
    (flight) =>
      clusterByPosition(flight, radiusM).map((c) =>
        groupOf(c.imageIndexes.flatMap((i) => byIndex.get(i) ?? [])),
      ),
  );
  units.sort((a, b) => byTimeThenIndex(firstOf(a), firstOf(b)));

  // 4 — greedy on the averages, then merge until nothing merges
  const groups: Group[] = [];
  for (const u of units) {
    const hit = groups.find((g) => haversineM(meanOf(g), meanOf(u)) <= radiusM);
    if (hit) absorb(hit, u);
    else groups.push(u);
  }
  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        const a = groups[i];
        const b = groups[j];
        if (!a || !b) continue;
        if (haversineM(meanOf(a), meanOf(b)) <= radiusM) {
          absorb(a, b);
          groups.splice(j, 1);
          merged = true;
          break outer;
        }
      }
    }
  }

  // 5 — the set-aside images: the nearest group; none → image by image
  if (groups.length === 0) {
    for (const c of clusterByPosition(aside, radiusM))
      groups.push(groupOf(c.imageIndexes.flatMap((i) => byIndex.get(i) ?? [])));
  } else {
    const means = groups.map(meanOf);
    for (const p of aside) {
      let best = 0;
      let bestD = Number.POSITIVE_INFINITY;
      means.forEach((m, k) => {
        const d = haversineM(m, p);
        if (d < bestD) {
          bestD = d;
          best = k;
        }
      });
      const g = groups[best];
      if (g) absorb(g, groupOf([p]));
    }
  }

  const out = groups.flatMap((g) => {
    const members = g.members.slice().sort(byTimeThenIndex);
    const first = members[0];
    if (!first) return [];
    const centroid = meanOf(g);
    let radius = 0;
    for (const m of members) radius = Math.max(radius, haversineM(centroid, m));
    return [
      {
        first,
        centroid,
        radiusM: radius,
        imageIndexes: members.map((m) => m.index),
      },
    ];
  });
  out.sort((x, y) => byTimeThenIndex(x.first, y.first));
  return out.map((c, k) => ({
    clusterId: `c${String(k + 1)}`,
    centroid: c.centroid,
    radiusM: c.radiusM,
    imageIndexes: c.imageIndexes,
  }));
}

function firstOf(g: Group): ClusterPoint {
  return (
    g.members.slice().sort(byTimeThenIndex)[0] ?? { index: 0, lat: 0, lon: 0 }
  );
}
